import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Package, Target, TrendingUp, Calendar, PackageCheck, RefreshCw, History, Download, Check, X, Award } from 'lucide-react';
import EditedNote from '../admin/fleet/EditedNote';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Compact pill rendering the parcel-performance grade for a given count. */
function GradeBadge({ grade, size = 'sm', showLabel = true, testId }) {
  if (!grade) return null;
  const dims = size === 'lg'
    ? { pad: 'px-3 py-1', letter: 'text-2xl', label: 'text-xs' }
    : size === 'md'
      ? { pad: 'px-2.5 py-0.5', letter: 'text-base', label: 'text-[10px]' }
      : { pad: 'px-2 py-0.5', letter: 'text-sm', label: 'text-[9px]' };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-bold text-white ${dims.pad}`}
      style={{ backgroundColor: grade.colour }}
      data-testid={testId}
      title={`${grade.letter} — ${grade.label} (${grade.parcels} parcels)`}
    >
      <Award className="h-3 w-3" />
      <span className={dims.letter}>{grade.letter}</span>
      {showLabel && <span className={`${dims.label} uppercase tracking-wider opacity-90`}>{grade.label}</span>}
    </span>
  );
}

/**
 * DailyTripsCard — driver parcel tracker.
 * Driver enters four numbers directly: Taken, Delivered, Collected, Returned.
 * Final Total = Delivered + Collected − Returned (clamped ≥ 0), tracked against daily target.
 */
export default function DailyTripsCard() {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [taken, setTaken] = useState('');
  const [delivered, setDelivered] = useState('');
  const [collected, setCollected] = useState('');
  const [returned, setReturned] = useState('');
  const [kilometres, setKilometres] = useState('');
  const [history, setHistory] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  const fetchHistory = useCallback(async (driverId) => {
    if (!driverId) return;
    try {
      const r = await axios.get(`${API_URL}/api/driver-trips/history/${driverId}?months=12`, { headers });
      setHistory(r.data);
    } catch { /* silent */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadHistoryCsv = async (driverId) => {
    try {
      const r = await axios.get(`${API_URL}/api/driver-trips/history/${driverId}/csv?months=12`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = 'Parcel_History.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/driver-trips/my`, { headers });
      setSummary(r.data);
      const t = r.data?.today?.trip;
      if (t) {
        setTaken(String(t.parcels_taken ?? ''));
        setDelivered(String(t.parcels_delivered ?? ''));
        setCollected(String(t.collections_total ?? ''));
        setReturned(String(t.parcels_returned ?? ''));
        setKilometres(String(t.kilometres ?? ''));
      } else {
        setTaken(''); setDelivered(''); setCollected(''); setReturned(''); setKilometres('');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load trips summary');
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => {
    if (summary?.driver_id) fetchHistory(summary.driver_id);
  }, [summary?.driver_id, fetchHistory]);

  const saveAll = async () => {
    const nums = { taken, delivered, collected, returned, kilometres };
    if (Object.values(nums).every((v) => v === '' || v === null)) {
      return toast.error('Enter at least one parcel count');
    }
    for (const [k, v] of Object.entries(nums)) {
      if (v !== '' && (isNaN(Number(v)) || Number(v) < 0)) {
        return toast.error(`${k} must be a non-negative number`);
      }
    }
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/driver-trips`, {
        parcels_taken:     taken     === '' ? undefined : Number(taken),
        parcels_delivered: delivered === '' ? undefined : Number(delivered),
        parcels_collected: collected === '' ? undefined : Number(collected),
        parcels_returned:  returned  === '' ? undefined : Number(returned),
        kilometres:        kilometres === '' ? undefined : Number(kilometres),
      }, { headers });
      toast.success("Today's parcels saved");
      window.dispatchEvent(new Event('driver-trips-saved'));
      await fetchSummary();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  if (loading || !summary) {
    return <Card><CardContent className="text-center py-6 text-muted-foreground">Loading trips…</CardContent></Card>;
  }

  const today = summary.today;
  const week = summary.week;
  const mo = summary.month_summary;
  const trip = today.trip;
  const targets = summary.targets;

  return (
    <div className="space-y-3">
      {/* Incentive banner — rich pacing view (delivered/collections split + behind/ahead + required daily rate) */}
      {(() => {
        const today = new Date();
        const y = today.getFullYear(); const m = today.getMonth();
        const daysInMonth = new Date(y, m + 1, 0).getDate();
        const daysElapsed = today.getDate();
        const daysRemaining = Math.max(daysInMonth - daysElapsed, 0);
        const expected = Math.round((daysElapsed / daysInMonth) * targets.monthly);
        const diff = mo.total - expected;                          // + ahead / − behind
        const remaining = Math.max(targets.monthly - mo.total, 0);
        const perDayNeeded = daysRemaining > 0 ? Math.ceil(remaining / daysRemaining) : remaining;
        const pct = Math.min(100, Math.round((mo.total / targets.monthly) * 100));
        const totalDeliveries = mo.delivered ?? 0;
        const totalCollections = mo.collections ?? 0;
        const qualified = mo.incentive_qualified;

        return (
          <Card
            className={qualified
              ? 'border-2 border-amber-400 bg-gradient-to-br from-amber-50 to-yellow-50'
              : 'border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-slate-50'}
            data-testid={qualified ? 'incentive-banner' : 'incentive-progress'}
          >
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Target className={`h-5 w-5 ${qualified ? 'text-amber-600' : 'text-indigo-600'}`} />
                <div className="font-bold text-slate-900">
                  {qualified ? '🎉 Monthly Incentive Qualified' : `🎯 Monthly Incentive — ${targets.monthly} Parcels`}
                </div>
              </div>

              <div className="flex items-baseline gap-2">
                <span className={`text-2xl font-extrabold ${qualified ? 'text-emerald-600' : 'text-indigo-700'}`}>
                  {mo.total.toLocaleString('en-ZA')}
                </span>
                <span className="text-sm text-slate-500">/ {targets.monthly.toLocaleString('en-ZA')} — {pct}% complete</span>
              </div>

              <div className="text-xs text-slate-700 flex flex-wrap gap-x-4 gap-y-1">
                <span>Deliveries: <b className="text-emerald-700">{totalDeliveries.toLocaleString('en-ZA')}</b></span>
                <span>Collections: <b className="text-indigo-700">{totalCollections.toLocaleString('en-ZA')}</b></span>
                {qualified
                  ? mo.incentive_amount > 0 && <span>Bonus: <b className="text-amber-700">R{Number(mo.incentive_amount).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</b></span>
                  : <span><b>{remaining.toLocaleString('en-ZA')}</b> parcels remaining</span>
                }
              </div>

              <Progress value={pct} className="h-3" />

              {/* Pacing line — hidden once qualified */}
              {!qualified && daysRemaining > 0 && (
                <div className={`text-xs flex items-center gap-2 rounded p-2 ${
                  diff >= 0 ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                            : 'bg-amber-50 text-amber-900 border border-amber-200'
                }`}>
                  {diff >= 0
                    ? <span>✅ <b>{diff}</b> ahead of expected progress ({expected.toLocaleString('en-ZA')} by day {daysElapsed}). Need <b>{perDayNeeded}</b> parcels/day for the last {daysRemaining} day{daysRemaining===1?'':'s'} to hit target.</span>
                    : <span>⚠️ <b>{Math.abs(diff)}</b> behind expected progress ({expected.toLocaleString('en-ZA')} by day {daysElapsed}). Need <b>{perDayNeeded}</b> parcels/day for the last {daysRemaining} day{daysRemaining===1?'':'s'} to make up target.</span>
                  }
                </div>
              )}
              {qualified && (
                <div className="text-xs text-amber-900 bg-amber-100 border border-amber-200 rounded p-2">
                  Your <b>R{Number(mo.incentive_amount || targets.incentive_amount).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</b> incentive will be auto-added to next month's payslip.
                </div>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* Today's card */}
      <Card data-testid="daily-trips-card">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Package className="h-4 w-4 text-cyan-600" />
            Today's Parcels
            <Badge className="ml-2 bg-cyan-100 text-cyan-800 border-cyan-300" variant="outline">
              Target {targets.daily}
            </Badge>
          </CardTitle>
          <Button size="sm" variant="ghost" onClick={fetchSummary}>
            <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Four direct number inputs — driver types each figure */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-cyan-700">Taken</label>
              <Input
                type="number" min="0" inputMode="numeric"
                value={taken}
                onChange={(e) => setTaken(e.target.value)}
                placeholder="0"
                className="mt-1 border-cyan-300 focus-visible:ring-cyan-500"
                data-testid="trip-taken-input"
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700">Delivered</label>
              <Input
                type="number" min="0" inputMode="numeric"
                value={delivered}
                onChange={(e) => setDelivered(e.target.value)}
                placeholder="0"
                className="mt-1 border-emerald-300 focus-visible:ring-emerald-500"
                data-testid="trip-delivered-input"
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-indigo-700">Collected</label>
              <Input
                type="number" min="0" inputMode="numeric"
                value={collected}
                onChange={(e) => setCollected(e.target.value)}
                placeholder="0"
                className="mt-1 border-indigo-300 focus-visible:ring-indigo-500"
                data-testid="trip-collected-input"
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-rose-700">Returned</label>
              <Input
                type="number" min="0" inputMode="numeric"
                value={returned}
                onChange={(e) => setReturned(e.target.value)}
                placeholder="0"
                className="mt-1 border-rose-300 focus-visible:ring-rose-500"
                data-testid="trip-returned-input"
              />
            </div>
            <div className="col-span-2">
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-700">Kilometres travelled</label>
              <Input
                type="number" min="0" step="0.1" inputMode="decimal"
                value={kilometres}
                onChange={(e) => setKilometres(e.target.value)}
                placeholder="0"
                className="mt-1 border-slate-300 focus-visible:ring-slate-500"
                data-testid="trip-km-input"
              />
            </div>
          </div>
          <Button
            onClick={saveAll}
            disabled={busy}
            className="w-full bg-slate-900 hover:bg-slate-800"
            data-testid="trip-save-all"
          >
            {busy ? 'Saving…' : trip ? "Update today's parcels" : "Save today's parcels"}
          </Button>

          {trip && (
            <>
              {/* Colour-coded readout of stored figures */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="rounded-lg border-2 border-cyan-200 bg-cyan-50 p-2 text-center" data-testid="stat-taken">
                  <div className="text-[10px] uppercase tracking-wider text-cyan-700 font-semibold">Taken</div>
                  <div className="text-2xl font-extrabold text-cyan-900">{trip.parcels_taken}</div>
                </div>
                <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50 p-2 text-center" data-testid="stat-delivered">
                  <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Delivered</div>
                  <div className="text-2xl font-extrabold text-emerald-900">{trip.parcels_delivered}</div>
                </div>
                <div className="rounded-lg border-2 border-indigo-200 bg-indigo-50 p-2 text-center" data-testid="stat-collected">
                  <div className="text-[10px] uppercase tracking-wider text-indigo-700 font-semibold">Collected</div>
                  <div className="text-2xl font-extrabold text-indigo-900">{trip.collections_total}</div>
                  {(summary?.collection_divisor || 1) > 1 && (
                    <div className="text-[9px] text-indigo-700 mt-0.5 font-semibold" data-testid="stat-collected-effective">
                      ÷{summary.collection_divisor} = <b>{trip.effective_collected ?? Math.floor((trip.collections_total || 0) / summary.collection_divisor)}</b> effective
                    </div>
                  )}
                </div>
                <div className="rounded-lg border-2 border-rose-200 bg-rose-50 p-2 text-center" data-testid="stat-returned">
                  <div className="text-[10px] uppercase tracking-wider text-rose-700 font-semibold">Returned</div>
                  <div className="text-2xl font-extrabold text-rose-900">{trip.parcels_returned}</div>
                </div>
              </div>

              {/* Final total against daily target */}
              <div className="rounded-lg border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-yellow-50 p-3" data-testid="stat-final-total">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold uppercase tracking-wider text-amber-800 flex items-center gap-1">
                    <Target className="h-3 w-3" /> Final Total
                  </span>
                  <span className={`text-xs font-semibold ${today.pct >= 100 ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {today.pct}%
                  </span>
                </div>
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className={`text-3xl font-extrabold ${today.pct >= 100 ? 'text-emerald-700' : 'text-amber-900'}`}>
                    {trip.total_parcels}
                  </span>
                  <span className="text-sm text-amber-800">/ {targets.daily} target</span>
                  <span className="text-[11px] text-slate-600 ml-auto italic">
                    {(summary?.collection_divisor || 1) > 1
                      ? <>Delivered {trip.parcels_delivered} + (Collected {trip.collections_total}÷{summary.collection_divisor} = {trip.effective_collected ?? Math.floor((trip.collections_total || 0) / summary.collection_divisor)}) − Returned {trip.parcels_returned}</>
                      : <>Delivered {trip.parcels_delivered} + Collected {trip.collections_total} − Returned {trip.parcels_returned}</>
                    }
                  </span>
                </div>
                <Progress value={today.pct} className="h-2 mt-2" data-testid="daily-progress" />
              </div>

              {/* Performance grade — based on successful deliveries only */}
              {today.grade && (
                <div
                  className="rounded-lg border-2 p-3 flex items-center justify-between gap-3"
                  style={{ borderColor: today.grade.colour, backgroundColor: `${today.grade.colour}14` }}
                  data-testid="daily-grade-card"
                >
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold flex items-center gap-1">
                      <Award className="h-3 w-3" /> Today's Performance Grade
                    </div>
                    <div className="text-xs text-slate-700 mt-0.5">
                      {(summary?.collection_divisor || 1) > 1 && (today.effective_delivered ?? today.grade.parcels) !== trip.parcels_delivered
                        ? <>Based on <b>{today.effective_delivered ?? today.grade.parcels} effective parcel{(today.effective_delivered ?? today.grade.parcels) === 1 ? '' : 's'}</b> ({trip.parcels_delivered} delivered + {trip.effective_collected ?? Math.floor((trip.collections_total || 0) / summary.collection_divisor)} from collections ÷ {summary.collection_divisor})</>
                        : <>Based on <b>{trip.parcels_delivered} successfully delivered</b> parcel{trip.parcels_delivered === 1 ? '' : 's'}</>
                      }
                    </div>
                  </div>
                  <GradeBadge grade={today.grade} size="lg" testId="daily-grade-badge" />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Rolling summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Card data-testid="week-summary-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Calendar className="h-4 w-4 text-purple-600" /> This Week
              <span className="ml-auto text-xs text-muted-foreground font-normal">{week.days} day{week.days === 1 ? '' : 's'}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-2">
            <div className="flex items-baseline justify-between">
              <span className="text-3xl font-extrabold text-purple-700">{week.total}</span>
              <span className="text-xs text-slate-500">of {targets.weekly}</span>
            </div>
            <Progress value={week.pct} className="h-2" />
            <div className="text-xs text-slate-600 flex items-center gap-1">
              <TrendingUp className="h-3 w-3" /> Rolling avg <b>{week.average_per_day}</b>/day
            </div>
            {week.grade && (
              <div className="pt-1.5 border-t flex items-center justify-between gap-2">
                <span className="text-[10px] text-slate-500 uppercase tracking-wider">Weekly grade</span>
                <GradeBadge grade={week.grade} size="md" testId="week-grade-badge" />
              </div>
            )}
          </CardContent>
        </Card>
        <Card data-testid="month-summary-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <PackageCheck className="h-4 w-4 text-amber-600" /> This Month
              <span className="ml-auto text-xs text-muted-foreground font-normal">{mo.days} day{mo.days === 1 ? '' : 's'}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-2">
            <div className="flex items-baseline justify-between">
              <span className={`text-3xl font-extrabold ${mo.incentive_qualified ? 'text-emerald-600' : 'text-amber-700'}`}>{mo.total}</span>
              <span className="text-xs text-slate-500">of {targets.monthly}</span>
            </div>
            <Progress value={mo.pct} className="h-2" />
            <div className="text-xs text-slate-600 flex items-center gap-1">
              <TrendingUp className="h-3 w-3" /> Rolling avg <b>{mo.average_per_day}</b>/day
            </div>
            {mo.grade && (
              <div className="pt-1.5 border-t flex items-center justify-between gap-2">
                <span className="text-[10px] text-slate-500 uppercase tracking-wider">Monthly grade</span>
                <GradeBadge grade={mo.grade} size="md" testId="month-grade-badge" />
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {(summary.trips || []).some((t) => t.edit_history?.length) && (
        <Card className="border-amber-300 bg-amber-50/50" data-testid="admin-corrections-card">
          <CardHeader className="pb-1"><CardTitle className="text-sm text-amber-900">Corrections made by admin this month</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {summary.trips.filter((t) => t.edit_history?.length).map((t) => (
              <div key={t.id} className="text-xs" data-testid={`correction-${t.date}`}>
                <span className="font-mono font-semibold text-slate-800">{t.date}</span>
                <EditedNote history={t.edit_history} />
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Monthly History (saved by month, auto-persisted) */}
      {history?.rows?.length > 0 && (
        <Card data-testid="monthly-history-card">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <History className="h-4 w-4 text-slate-500" /> Monthly History (last 12)
            </CardTitle>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="outline" onClick={() => setHistoryOpen((v) => !v)} data-testid="toggle-history">
                {historyOpen ? 'Hide' : 'Show'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => downloadHistoryCsv(summary.driver_id)} data-testid="download-history">
                <Download className="h-3 w-3 mr-1" /> CSV
              </Button>
            </div>
          </CardHeader>
          {historyOpen && (
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 text-slate-700">
                  <tr>
                    <th className="text-left px-2 py-1.5">Month</th>
                    <th className="text-center px-2 py-1.5">Days</th>
                    <th className="text-center px-2 py-1.5">Taken</th>
                    <th className="text-center px-2 py-1.5 text-emerald-700">Delivered</th>
                    <th className="text-center px-2 py-1.5 text-indigo-700">Collected</th>
                    <th className="text-center px-2 py-1.5 text-rose-700">Returned</th>
                    <th className="text-right px-2 py-1.5">Total</th>
                    <th className="text-center px-2 py-1.5">Grade</th>
                    <th className="text-center px-2 py-1.5">Target</th>
                  </tr>
                </thead>
                <tbody>
                  {[...history.rows].reverse().map((r) => (
                    <tr key={r.month} className={`border-t ${r.met ? 'bg-emerald-50/40' : ''}`} data-testid={`history-row-${r.month}`}>
                      <td className="px-2 py-1.5 font-mono">{r.month}</td>
                      <td className="text-center px-2 py-1.5">{r.days}</td>
                      <td className="text-center px-2 py-1.5">{r.taken}</td>
                      <td className="text-center px-2 py-1.5 text-emerald-700 font-semibold">{r.delivered}</td>
                      <td className="text-center px-2 py-1.5 text-indigo-700 font-semibold">{r.collections}</td>
                      <td className="text-center px-2 py-1.5 text-rose-700">{r.returned}</td>
                      <td className="text-right px-2 py-1.5 font-extrabold">{r.total}</td>
                      <td className="text-center px-2 py-1.5" data-testid={`history-grade-${r.month}`}>
                        {r.grade
                          ? <GradeBadge grade={r.grade} size="sm" showLabel={false} />
                          : <span className="text-slate-400 text-[10px]">—</span>}
                      </td>
                      <td className="text-center px-2 py-1.5">
                        {r.met
                          ? <Check className="h-4 w-4 text-emerald-600 inline" />
                          : <X className="h-4 w-4 text-slate-400 inline" />
                        }
                        <span className="ml-1 text-[10px] text-slate-500">{r.pct}%</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                {history.yearly && (
                  <tfoot className="bg-slate-900 text-white font-bold" data-testid="history-yearly-total">
                    <tr>
                      <td className="px-2 py-2">12-month total</td>
                      <td className="text-center px-2 py-2">{history.yearly.days}</td>
                      <td className="text-center px-2 py-2">{history.yearly.taken.toLocaleString('en-ZA')}</td>
                      <td className="text-center px-2 py-2 text-emerald-300">{history.yearly.delivered.toLocaleString('en-ZA')}</td>
                      <td className="text-center px-2 py-2 text-indigo-300">{history.yearly.collections.toLocaleString('en-ZA')}</td>
                      <td className="text-center px-2 py-2 text-rose-300">{history.yearly.returned.toLocaleString('en-ZA')}</td>
                      <td className="text-right px-2 py-2 text-amber-300">{history.yearly.total.toLocaleString('en-ZA')}</td>
                      <td className="text-center px-2 py-2" />
                      <td className="text-center px-2 py-2 text-[10px]">
                        {history.yearly.months_hit}/{history.yearly.months_total} months hit · {history.yearly.pct}%
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </CardContent>
          )}
        </Card>
      )}

      {/* Grading scale reference */}
      {summary?.grade_scale?.length > 0 && (
        <Card data-testid="grade-scale-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Award className="h-4 w-4 text-emerald-600" /> Parcel Performance Grading Scale
              <span className="ml-auto text-[10px] text-slate-500 font-normal">Grade = successful deliveries per working day</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1.5">
              {summary.grade_scale.map((row, i) => {
                const upper = i === 0 ? null : summary.grade_scale[i - 1].threshold - 1;
                const range = upper === null
                  ? `${row.threshold}+`
                  : row.threshold === 0
                    ? `Below 50`
                    : `${row.threshold}–${upper}`;
                return (
                  <div
                    key={row.letter}
                    className="rounded border px-2 py-1 flex items-center gap-2"
                    style={{ borderColor: row.colour }}
                    data-testid={`scale-row-${row.letter}`}
                  >
                    <span
                      className="rounded-full font-bold text-white px-2 py-0.5 text-xs min-w-[2rem] text-center"
                      style={{ backgroundColor: row.colour }}
                    >{row.letter}</span>
                    <div className="min-w-0 flex-1">
                      <div className="text-[11px] text-slate-800 font-semibold truncate">{row.label}</div>
                      <div className="text-[10px] text-slate-500 font-mono">{range} parcels</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add collection dialog removed — collected is now a direct number input in the Today's Parcels card */}
    </div>
  );
}
